/**
 * Application State Store
 * Global state management using Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Version check - increment this to force reset old cached settings
const SETTINGS_VERSION = 2; // Bumped to clear deprecated model settings

export const useAppStore = create(
  persist(
    (set, get) => ({
      version: SETTINGS_VERSION,
      // Cloudflare Configuration
      cloudflare: {
        accountId: '',
        apiToken: '',
        workerUrl: '',
        isConfigured: false
      },
      setCloudflareConfig: (config) =>
        set((state) => ({
          cloudflare: {
            ...state.cloudflare,
            ...config,
            isConfigured: !!(config.workerUrl)
          }
        })),

      // Ollama Configuration
      ollama: {
        baseUrl: 'https://ollama-api.damineweb.work',
        enabled: true
      },
      setOllamaConfig: (config) =>
        set((state) => ({
          ollama: { ...state.ollama, ...config }
        })),

      // Selected Model
      selectedModel: 'deepseek-v4-pro:cloud',
      setSelectedModel: (modelId) =>
        set({ selectedModel: modelId }),

      // Conversations
      conversations: [],
      activeConversationId: null,

      addConversation: (conversation) =>
        set((state) => ({
          conversations: [conversation, ...state.conversations],
          activeConversationId: conversation.id
        })),

      updateConversation: (id, updates) =>
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === id ? { ...conv, ...updates } : conv
          )
        })),

      deleteConversation: (id) =>
        set((state) => ({
          conversations: state.conversations.filter((conv) => conv.id !== id),
          activeConversationId:
            state.activeConversationId === id
              ? state.conversations[0]?.id || null
              : state.activeConversationId
        })),

      setActiveConversation: (id) =>
        set({ activeConversationId: id }),

      getActiveConversation: () => {
        const state = get();
        return state.conversations.find(
          (conv) => conv.id === state.activeConversationId
        );
      },

      // Messages
      addMessage: (conversationId, message) =>
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId
              ? { ...conv, messages: [...conv.messages, message] }
              : conv
          )
        })),

      updateMessage: (conversationId, messageIndex, updates) =>
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId
              ? {
                  ...conv,
                  messages: conv.messages.map((msg, idx) =>
                    idx === messageIndex ? { ...msg, ...updates } : msg
                  )
                }
              : conv
          )
        })),

      // Settings
      settings: {
        // Auto-compaction settings
        autoCompact: true,
        compactionThreshold: 100000, // tokens
        compactionTarget: 30000, // tokens

        // Memory settings (disabled by default - enable in settings after testing)
        enableMemory: false,
        maxMemoryItems: 1000,
        similarityThreshold: 0.7,

        // Agent settings
        autoCreateAgents: true,
        maxConcurrentAgents: 5,
        agentTimeout: 300000,

        // Model settings
        defaultModel: '@cf/meta/llama-3.3-70b-instruct-fp8-fast',
        fastModel: '@cf/meta/llama-3.2-3b-instruct',
        codingModel: '@cf/deepseek-ai/deepseek-coder-6.7b-instruct-awq',
        agentModel: '@hf/nousresearch/hermes-2-pro-mistral-7b',
        embeddingModel: '@cf/baai/bge-base-en-v1.5',

        // UI settings
        theme: 'dark',
        codeHighlighting: true,
        streamingResponses: true,
        showTokenCount: true,

        // Advanced settings
        temperature: 0.7,
        maxTokens: 4000,
        topP: 0.9
      },

      updateSettings: (updates) =>
        set((state) => ({
          settings: { ...state.settings, ...updates }
        })),

      // UI State
      ui: {
        sidebarOpen: true,
        settingsOpen: false,
        agentsPanelOpen: false,
        memoryPanelOpen: false
      },

      toggleSidebar: () =>
        set((state) => ({
          ui: { ...state.ui, sidebarOpen: !state.ui.sidebarOpen }
        })),

      toggleSettings: () =>
        set((state) => ({
          ui: { ...state.ui, settingsOpen: !state.ui.settingsOpen }
        })),

      toggleAgentsPanel: () =>
        set((state) => ({
          ui: { ...state.ui, agentsPanelOpen: !state.ui.agentsPanelOpen }
        })),

      toggleMemoryPanel: () =>
        set((state) => ({
          ui: { ...state.ui, memoryPanelOpen: !state.ui.memoryPanelOpen }
        })),

      // Statistics
      statistics: {
        totalMessages: 0,
        totalTokens: 0,
        totalCompactions: 0,
        totalAgents: 0,
        tokensSaved: 0
      },

      updateStatistics: (updates) =>
        set((state) => ({
          statistics: { ...state.statistics, ...updates }
        })),

      incrementStatistic: (key, value = 1) =>
        set((state) => ({
          statistics: {
            ...state.statistics,
            [key]: state.statistics[key] + value
          }
        }))
    }),
    {
      name: 'cloudflare-dev-agent-storage',
      version: SETTINGS_VERSION,
      migrate: (persistedState, version) => {
        // If version mismatch, reset settings to defaults
        if (version !== SETTINGS_VERSION) {
          console.log('Migrating settings from version', version, 'to', SETTINGS_VERSION);
          return {
            ...persistedState,
            version: SETTINGS_VERSION,
            settings: {
              // Reset to new defaults
              autoCompact: true,
              compactionThreshold: 100000,
              compactionTarget: 30000,
              enableMemory: false, // Disabled by default
              maxMemoryItems: 1000,
              similarityThreshold: 0.7,
              autoCreateAgents: true,
              maxConcurrentAgents: 5,
              agentTimeout: 300000,
              defaultModel: '@cf/meta/llama-3.3-70b-instruct-fp8-fast',
              fastModel: '@cf/meta/llama-3.2-3b-instruct',
              codingModel: '@cf/deepseek-ai/deepseek-coder-6.7b-instruct-awq',
              agentModel: '@hf/nousresearch/hermes-2-pro-mistral-7b',
              embeddingModel: '@cf/baai/bge-base-en-v1.5',
              theme: 'dark',
              codeHighlighting: true,
              streamingResponses: true,
              showTokenCount: true,
              temperature: 0.7,
              maxTokens: 4000,
              topP: 0.9
            }
          };
        }
        return persistedState;
      },
      partialize: (state) => ({
        version: state.version,
        cloudflare: state.cloudflare,
        conversations: state.conversations,
        activeConversationId: state.activeConversationId,
        settings: state.settings,
        statistics: state.statistics
      })
    }
  )
);
